import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  Divider,
  Button,
} from "@tiny/components";
import { useAITrafficSegmentDetailsDialog } from "./AITrafficSegmentDetailsDialog.hooks";
import SegmentRow from "./SegmentRow/SegmentRow";
import { useTinyHost } from "@tiny/host-contract";
import { AITrafficSegmentDetailsDialogProps } from "./AITrafficSegmentDetailsDialog.types";
import { useStyles } from "./AITrafficSegmentDetailsDialog.styles";
import AudienceSummaryDialog from "../AudienceSummaryDialog/AudienceSummaryDialog";

function AITrafficSegmentDetailsDialog(
  props: AITrafficSegmentDetailsDialogProps
) {
  const { open, onClose } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
    >
      <AITrafficSegmentDetailsDialogContent {...props} />
    </Dialog>
  );
}

function AITrafficSegmentDetailsDialogContent(
  props: AITrafficSegmentDetailsDialogProps
) {
  const { onClose, aiTrafficControlId, open } = props;
  const { navigate } = useTinyHost();
  const classes = useStyles();
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<string[]>([]);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);

  const { trafficControlData, isLoadingSegments } =
    useAITrafficSegmentDetailsDialog({
      aiTrafficControlId,
      open,
    });

  const handleSegmentClick = (segmentId: string) => {
    navigate.to(`/main/audience/segments/${segmentId}`);
    onClose?.();
  };

  const handleToggleSelect = (segmentId: string) => {
    setSelectedSegmentIds((prev) => {
      if (prev.includes(segmentId)) {
        return prev.filter((id) => id !== segmentId);
      } else {
        return [...prev, segmentId];
      }
    });
  };

  const handleOpenSummary = () => {
    setShowSummaryDialog(true);
  };

  const handleCloseSummary = () => {
    setShowSummaryDialog(false);
  };

  const renderContent = () => {
    if (isLoadingSegments) {
      return (
        <Box className={classes.emptyState}>
          <CircularProgress />
        </Box>
      );
    }

    if (!trafficControlData) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body2" color="grey" tone={900}>
            No traffic control data available
          </Typography>
        </Box>
      );
    }

    const { segments = [] } = trafficControlData;

    const engager = segments.find((s) => s.priority === -1 && s.enabled);
    const customs = segments
      .filter((s) => s.priority !== -1)
      .sort((a, b) => a.priority - b.priority);

    return (
      <Box className={classes.dialogContainer}>
        {engager && (
          <>
            <Box className={classes.titleContainer}>
              <Typography tone={900} color="grey">
                Engager Segment
              </Typography>
            </Box>
            <SegmentRow
              key={engager.segmentId}
              segment={engager}
              onClick={() => handleSegmentClick(engager.segmentId)}
              type="engager"
              isLast={true}
              selected={selectedSegmentIds.includes(engager.segmentId)}
              onToggleSelect={handleToggleSelect}
            />
          </>
        )}

        {customs.length > 0 && (
          <>
            <Box className={classes.titleContainer}>
              <Typography tone={900} color="grey">
                Segments (Priority Order)
              </Typography>
            </Box>
            {customs.map((segment, idx) => (
              <SegmentRow
                key={segment.segmentId}
                segment={segment}
                onClick={() => handleSegmentClick(segment.segmentId)}
                type="custom"
                isLast={idx === customs.length - 1}
                selected={selectedSegmentIds.includes(segment.segmentId)}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <DialogTitle subtitle="Select segments to view summary">
        AI Traffic Segment Details
      </DialogTitle>

      {renderContent()}

      {selectedSegmentIds.length > 0 && (
        <Box px={6} pb={6}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenSummary}
            fullWidth
          >
            View Audience Summary ({selectedSegmentIds.length})
          </Button>
        </Box>
      )}
      <AudienceSummaryDialog
        open={showSummaryDialog}
        onClose={handleCloseSummary}
        segmentIds={selectedSegmentIds}
      />
    </>
  );
}

export default AITrafficSegmentDetailsDialog;
